import type { RouteLocationNormalizedLoaded } from 'vue-router';
import { COST_CONTROLLING_TAB_IDS } from '@/components/mfh/one-mfh/renovation-project/cost-breakdown/cost-controlling.helpers';

const MFH_DETAIL_TABS = ['property', 'location', 'sourcing', 'financial'] as const;

export function mfhRamEmbedPropsForMfhDetailRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    const tab = route.query.tab;
    let pathAndSearch = `/assets/${id}/details/property`;
    if (typeof tab === 'string' && (MFH_DETAIL_TABS as readonly string[]).includes(tab)) {
        pathAndSearch = `/assets/${id}/details/${tab}`;
    }
    return {
        cockpitFeature: 'mfh-detail' as const,
        persistenceKey: id,
        pathAndSearch,
        title: 'Asset details (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForAddendaRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    const addendumIdRaw = route.query.addendumId;
    const addendumId = typeof addendumIdRaw === 'string' && /^\d+$/.test(addendumIdRaw) ? addendumIdRaw : null;
    const contractIdRaw = route.query.contractId;
    const contractId = typeof contractIdRaw === 'string' && /^\d+$/.test(contractIdRaw) ? contractIdRaw : null;
    const pathAndSearch =
        addendumId != null && contractId != null
            ? `/assets/${id}/transformation/contracts/${contractId}/addenda/${addendumId}`
            : addendumId != null
              ? `/assets/${id}/transformation/addenda/${addendumId}`
              : `/assets/${id}/transformation/contracts`;
    return {
        persistenceKey: id,
        pathAndSearch,
        title: 'Addenda (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForConstructionPlanRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    const planIdRaw = route.query.planId;
    const planId = typeof planIdRaw === 'string' && /^\d+$/.test(planIdRaw) ? planIdRaw : null;
    const basePath = planId != null ? `/assets/${id}/construction-plans/${planId}` : `/assets/${id}/construction-plans`;

    // Forward optional deep-link query (e.g. open a specific AI comparison report) to the RAM page.
    const search = new URLSearchParams();
    for (const key of ['tab', 'compareSub', 'view', 'version', 'compareWith'] as const) {
        const value = route.query[key];
        if (typeof value === 'string' && value.length > 0) {
            search.set(key, value);
        }
    }
    const queryString = search.toString();
    const pathAndSearch = queryString ? `${basePath}?${queryString}` : basePath;

    return {
        persistenceKey: id,
        pathAndSearch,
        title: 'Construction plan (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForCapexEeCalculatorRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/capex-ee-calculator`,
        title: 'CapEx EE calculator (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForContractsRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    const contractIdRaw = route.query.contractId;
    const contractId = typeof contractIdRaw === 'string' && /^\d+$/.test(contractIdRaw) ? contractIdRaw : null;
    const pathAndSearch = contractId != null ? `/assets/${id}/transformation/contracts/${contractId}` : `/assets/${id}/transformation/contracts`;
    return {
        persistenceKey: id,
        pathAndSearch,
        title: 'Contracts (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForCostControllingRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    const base = `/assets/${id}/transformation/cost-tracking`;
    const tab = route.query.tab;
    let pathAndSearch = base;
    if (typeof tab === 'string' && (COST_CONTROLLING_TAB_IDS as readonly string[]).includes(tab)) {
        pathAndSearch = `${base}?tab=${encodeURIComponent(tab)}`;
    }
    return {
        cockpitFeature: 'renovation-cost-controlling' as const,
        persistenceKey: id,
        pathAndSearch,
        title: 'Cost controlling (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForRenovationProjectsRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        cockpitFeature: 'renovation-projects' as const,
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/transformation/projects`,
        title: 'Renovation projects (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForContactsRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        cockpitFeature: 'contacts' as const,
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/contacts`,
        title: 'Contacts (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForTaskMilestonesRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        cockpitFeature: 'task-milestones' as const,
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/milestones/reporting`,
        title: 'Milestones reporting (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForRentRollOverviewRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        cockpitFeature: 'acquisition-rent-roll' as const,
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/rent-roll`,
        title: 'Rent roll (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForRentRollDetailRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    const versionIdRaw = route.params.rentRollVersionId;
    const versionId = typeof versionIdRaw === 'string' && /^\d+$/.test(versionIdRaw) ? versionIdRaw : '';
    const pathAndSearch = versionId !== '' ? `/assets/${id}/rent-roll/${versionId}` : `/assets/${id}/rent-roll`;
    return {
        cockpitFeature: 'rent-roll-detail' as const,
        persistenceKey: id,
        pathAndSearch,
        title: 'Rent roll (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForPlacementRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/placement`,
        title: 'Placement (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForAccountingRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        cockpitFeature: 'accounting' as const,
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/accounting`,
        title: 'Accounting (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForEstatesRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        cockpitFeature: 'estates' as const,
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/estates`,
        title: 'Estates (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForTransformationElementsIfcRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        cockpitFeature: 'elements-ifc' as const,
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/transformation/elements-ifc`,
        title: 'Elements / IFC (RAM)',
        wrapInCard: true,
    };
}

const INVESTMENT_CASE_DETAIL_TABS = ['inputs', 'sheet', 'results', 'notes'] as const;

export function mfhRamEmbedPropsForInvestmentCaseOverviewRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        cockpitFeature: 'investment-cases' as const,
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/investment-cases`,
        title: 'Investment cases (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForInvestmentCaseDetailRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    const icIdRaw = route.params.mfhInvestmentCaseId;
    const icId = typeof icIdRaw === 'string' && /^\d+$/.test(icIdRaw) ? icIdRaw : '';
    let pathAndSearch = icId !== '' ? `/assets/${id}/investment-cases/${icId}` : `/assets/${id}/investment-cases`;
    const tab = route.query.tab;
    if (typeof tab === 'string' && (INVESTMENT_CASE_DETAIL_TABS as readonly string[]).includes(tab)) {
        pathAndSearch = `${pathAndSearch}?tab=${encodeURIComponent(tab)}`;
    }
    return {
        cockpitFeature: 'investment-case-detail' as const,
        persistenceKey: id,
        pathAndSearch,
        title: 'Investment case (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForBaseInvestmentCaseConfigureRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    const icIdRaw = route.params.mfhInvestmentCaseId;
    const icId = typeof icIdRaw === 'string' && /^\d+$/.test(icIdRaw) ? icIdRaw : '';
    const pathAndSearch = icId !== '' ? `/assets/${id}/base-investment-cases/${icId}/configure` : `/assets/${id}/investment-cases`;
    return {
        // No Cockpit Vue fallback — base configure is RAM-only. Omit cockpitFeature so iframe always shows.
        persistenceKey: id,
        pathAndSearch,
        title: 'Base investment case (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForRenovationProjectsHistoryRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        cockpitFeature: 'renovation-history' as const,
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/history/processes?tab=transformation`,
        title: 'Transformation history (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForSignOffEnvelopesOverviewRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        cockpitFeature: 'sign-off-envelopes-overview' as const,
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/approvals`,
        title: 'Approvals (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForSignOffEnvelopeCreateRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    return {
        cockpitFeature: 'sign-off-envelope-create' as const,
        persistenceKey: id,
        pathAndSearch: `/assets/${id}/approvals/create`,
        title: 'Create approval (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForSignOffEnvelopeDetailRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    const envelopeIdRaw = route.params.signOffEnvelopeId;
    const envelopeId = typeof envelopeIdRaw === 'string' && /^\d+$/.test(envelopeIdRaw) ? envelopeIdRaw : '';
    const pathAndSearch = envelopeId !== '' ? `/assets/${id}/approvals/${envelopeId}` : `/assets/${id}/approvals`;
    return {
        cockpitFeature: 'sign-off-envelope-detail' as const,
        persistenceKey: id,
        pathAndSearch,
        title: 'Approval detail (RAM)',
        wrapInCard: true,
    };
}

export function mfhRamEmbedPropsForSignOffEnvelopeAdditionalDocumentRoute(route: RouteLocationNormalizedLoaded) {
    const id = +route.params.mfhId;
    const envelopeIdRaw = route.params.signOffEnvelopeId;
    const envelopeId = typeof envelopeIdRaw === 'string' && /^\d+$/.test(envelopeIdRaw) ? envelopeIdRaw : '';
    const pathAndSearch = envelopeId !== '' ? `/assets/${id}/approvals/${envelopeId}/additional-document` : `/assets/${id}/approvals`;
    return {
        cockpitFeature: 'sign-off-envelope-additional-document' as const,
        persistenceKey: id,
        pathAndSearch,
        title: 'Additional documents (RAM)',
        wrapInCard: true,
    };
}
